import React from "react"
import { User, MapPin, Mail, Phone, MessageCircle, Clock, Map, Send } from "lucide-react"
// Ascending two levels to reach the types directory
import { ContactData } from "../../types/objects"
import { SectionHeading } from "../ui/SectionHeading"

/**
 * Props interface for Contact component
 */
interface ContactProps {
	data?: ContactData
}

/**
 * Contact Component — contact details, quick actions and an embedded Google Map.
 */
export const Contact: React.FC<ContactProps> = ({ data = {} }) => {
	const safeEmail = (data.email || "").trim()
	const address = (data.address || "Prague, Czechia").trim()
	const safePhone = (data.phone || "").trim()
	const phoneDigits = safePhone.replace(/\s+/g, "")
	const telHref = safePhone ? `tel:${phoneDigits.startsWith("+") ? phoneDigits : `+420${phoneDigits}`}` : "#contact"
	const whatsapp = (data.whatsapp || "").trim()
	const whatsappHref = whatsapp ? `https://wa.me/${whatsapp}` : ""
	const hours = data.hours || "pondělí–pátek 9:00–17:00"
	const responseTime = data.responseTime || "odpovídáme do 24 hodin v pracovní dny"
	const slaCritical = data.slaCritical || "reakce do 4 hodin v pracovní dny"
	const slaStandard = data.slaStandard || "do 1 pracovního dne"

	/**
	 * Pre-defined mailto link with encoded subject and body
	 */
	const mailtoHref = safeEmail
		? `mailto:${safeEmail}?subject=${encodeURIComponent("Poptávka z webu")}&body=${encodeURIComponent(
				"Dobrý den,\n\nrád/a bych probral/a...\n\nDěkuji."
			)}`
		: "#contact"

	// Map links using the provided address
	const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
	const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address)}&t=&z=13&ie=UTF8&iwloc=&output=embed`

	return (
		<section id="contact" className="lp-section lp-section--dark">
			<div className="lp-container">
				<SectionHeading
					eyebrow="Kontakt"
					title="Pojďme probrat váš projekt"
					lead="Popište nám svůj problém nebo záměr – ozveme se vám do 1 pracovního dne a domluvíme nezávaznou konzultaci."
				/>

				<div className="lp-contact">
					{/* LEFT — Contact info card */}
					<div className="lp-card lp-card--static lp-contact__card" data-reveal="left">
						<h3>Kontaktní informace</h3>

						<ul className="lp-contact__list">
							<li className="lp-contact__item">
								<span className="lp-icon-badge" aria-hidden="true"><User size={18} /></span>
								<div>
									<strong>Kontaktní osoba</strong>
									<span>Martin Šnajdr</span>
								</div>
							</li>
							<li className="lp-contact__item">
								<span className="lp-icon-badge" aria-hidden="true"><MapPin size={18} /></span>
								<div>
									<strong>Adresa</strong>
									<span>{address}</span>
								</div>
							</li>
							<li className="lp-contact__item">
								<span className="lp-icon-badge" aria-hidden="true"><Mail size={18} /></span>
								<div>
									<strong>Email</strong>
									{safeEmail ? <a href={`mailto:${safeEmail}`}>{safeEmail}</a> : <span>—</span>}
								</div>
							</li>
							{safePhone && (
								<li className="lp-contact__item">
									<span className="lp-icon-badge" aria-hidden="true"><Phone size={18} /></span>
									<div>
										<strong>Telefon</strong>
										<a href={telHref}>{safePhone}</a>
									</div>
								</li>
							)}
						</ul>

						<div className="lp-contact__chips">
							<a href={mapsLink} target="_blank" rel="noopener noreferrer" className="lp-chip" aria-label="Otevřít v Mapách">
								<Map size={16} /> Trasa / Mapy
							</a>
							{safePhone && (
								<a href={telHref} className="lp-chip" aria-label="Zavolat">
									<Phone size={16} /> Zavolat
								</a>
							)}
							{whatsappHref && (
								<a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="lp-chip" aria-label="Napsat na WhatsApp">
									<MessageCircle size={16} /> WhatsApp
								</a>
							)}
						</div>

						{safeEmail && (
							<a href={mailtoHref} className="lp-btn lp-btn--primary" style={{ alignSelf: "flex-start" }} aria-label="Napsat poptávku">
								<Send size={18} /> Napsat poptávku
							</a>
						)}

						{/* Support availability — mirrors the static "Dostupnost podpory" card in public/index.html */}
						<div className="lp-contact__support">
							<h4><Clock size={18} /> Dostupnost podpory</h4>
							<dl>
								<dt>Telefon a WhatsApp:</dt>
								<dd>{hours}</dd>
								<dt>E-mail:</dt>
								<dd>{responseTime}</dd>
								<dt>Kritický incident (SLA):</dt>
								<dd>{slaCritical}</dd>
								<dt>Standardní požadavek (SLA):</dt>
								<dd>{slaStandard}</dd>
							</dl>
						</div>
					</div>

					{/* RIGHT — Google Map Card */}
					<div className="lp-card lp-card--static lp-contact__map" data-reveal="right" aria-label="Mapa adresy">
						<iframe
							title="Google Map"
							src={mapSrc}
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
						/>
					</div>
				</div>
			</div>
		</section>
	)
}
